import { Box, Group } from '@mantine/core';
import Button from 'components/Button';
import { IconCheck, IconX } from '@tabler/icons-react';
import ButtonWithConfirm from 'components/modals/confirm/ButtonWithConfirm';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { noop } from 'lodash';
import { useNavigate } from 'react-router-dom';

export interface FormFooterProps {
  loading?: boolean;
  primaryIcon?: React.ReactNode;
  primaryButtonText?: string;
  hidePrimaryButton?: boolean;
  secondaryIcon?: React.ReactNode;
  secondaryButtonText?: string;
  hideSecondaryButton?: boolean;
  skipSecondaryButtonConfirm?: boolean;
  closeConfirmTitle?: string;
  closeConfirmMessage?: string;
  rightSide?: React.ReactNode;
  pb?: number;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
}

/**
 * The form footer. Allows to submit or cancel the form.
 */
export default function FormFooter({
  loading = false,
  primaryButtonText = 'Uložiť',
  hidePrimaryButton = false,
  primaryIcon = <IconCheck stroke="1.5" height={24} width={24} />,
  secondaryIcon = <IconX stroke="1.5" height={24} width={24} />,
  secondaryButtonText = 'Zrušiť',
  hideSecondaryButton = false,
  skipSecondaryButtonConfirm = false,
  closeConfirmTitle = 'Zrušenie formulára',
  closeConfirmMessage = 'Naozaj chcete odísť? Všetky neuložené zmeny budú stratené.',
  rightSide,
  pb = 24,
  onPrimaryClick = noop,
  onSecondaryClick,
}: FormFooterProps = {}) {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsiveDesign();

  return (
    <Box
      data-e2e-capture-role="form-footer"
      w="100%"
      pos="sticky"
      bottom={0}
      pb={pb}
      mb={-pb}
      bg="gray.1"
      sx={{ zIndex: 50 }}
    >
      <Group
        w="100%"
        position={isMobile ? 'left' : 'apart'}
        align={isMobile ? 'stretch' : 'center'}
        noWrap={!isMobile}
        spacing={12}
        bg="white"
        py={16}
        px={isMobile ? 16 : isTablet ? 20 : 40}
        sx={(theme) => ({
          zIndex: 50,
          borderTop: `1px solid ${theme.colors.gray[1]}`,
          borderBottomLeftRadius: theme.radius.sm,
          borderBottomRightRadius: theme.radius.sm,
          // boxShadow: '0px -4px 12px 0px rgba(197, 198, 211, 0.40)',
        })}
      >
        <Group
          spacing={16}
          w={isMobile ? '100%' : undefined}
          noWrap={!isMobile}
          sx={(theme) => ({ borderBottomLeftRadius: theme.radius.sm, borderBottomRightRadius: theme.radius.sm })}
        >
          {!hidePrimaryButton && (
            <Button
              type="submit"
              variant="primary"
              leftIcon={primaryIcon}
              size="md"
              loading={loading}
              fullWidth={isMobile}
              onClick={onPrimaryClick}
            >
              {primaryButtonText}
            </Button>
          )}
          {!hideSecondaryButton && (
            <ButtonWithConfirm
              variant="tertiary"
              confirmTitle={closeConfirmTitle}
              confirmMessage={closeConfirmMessage}
              leftIcon={secondaryIcon}
              size="md"
              loading={loading}
              loaderPosition="center"
              loaderProps={{ size: 0 }}
              skipConfirm={skipSecondaryButtonConfirm}
              fullWidth={isMobile}
              onConfirm={() => (onSecondaryClick ? onSecondaryClick() : navigate(-1))}
            >
              {secondaryButtonText}
            </ButtonWithConfirm>
          )}
        </Group>
        {rightSide && <Box w={isMobile ? '100%' : undefined}>{rightSide}</Box>}
      </Group>
    </Box>
  );
}
